import { useRef } from 'react'
import { TechnicianAppointmentsItem, TaskListItem } from '@myapp/contract'

interface Snapshot {
  dayIsos: string[]
  technicianData: TechnicianAppointmentsItem | null
  scheduledTasks: TaskListItem[]
  unscheduledTasks: TaskListItem[]
  departureReminderTasks: TaskListItem[]
}

interface Input extends Snapshot {
  isTransitioning: boolean
}

interface Output extends Snapshot {
  isTransitioning: boolean
}

/**
 * Keeps showing the last "consistent snapshot" (dayIsos + matching data)
 * while a navigation fetch is in-flight, then atomically switches to the
 * new data once it arrives. This eliminates the flash of empty columns.
 */
export function useBufferedCalendarData({
  dayIsos,
  technicianData,
  scheduledTasks,
  unscheduledTasks,
  departureReminderTasks,
  isTransitioning,
}: Input): Output {
  const snapshotRef = useRef<Snapshot | null>(null)

  // Build the candidate snapshot from current props
  const current: Snapshot = {
    dayIsos,
    technicianData,
    scheduledTasks,
    unscheduledTasks,
    departureReminderTasks,
  }

  // Commit to the snapshot when data is stable (not transitioning)
  // OR on the very first render (ref is still null)
  if (!isTransitioning || snapshotRef.current === null) {
    snapshotRef.current = current
  }

  return {
    ...snapshotRef.current,
    isTransitioning,
  }
}
